// ═══════════════════════════════════════════════════════════════════════════
// FeedConflicts — the typed days the feed replaced, with the super admin's
// per-day "keep typed rows" override (plan 7.5 step 8; contract 4.6, 5.9 and
// 5.10; owner decision 21 Oct 2026).
// ═══════════════════════════════════════════════════════════════════════════
// Replacing is the default, and the server records it itself: from each
// route's feed start the feed takes the channel-day, and the typed, synced or
// statement rows it replaced are kept for restore. Keeping a day's typed rows
// is a super admin only override: the held rows come back and the feed writes
// nothing for that day. This screen never replaces anything itself; its only
// writes are the override and its undo.
//
// Controlled editor. The parent passes the listed days of one connection
// (feedApi listConflicts) and the account's locations (for names, channel
// names and each location's currency). Only an explicit "keep" is a keep;
// any other choice, null included, reads as "replace" (contract 4.6).
// onSave(choices) receives only the days whose override the admin changed, as
// the saveDayChoices list:
//   [{ channelId, logDate, choice: "keep" | "replace" }]   (1 to 366)
// "keep" for a newly ticked day, "replace" for a newly unticked one.
// onRange({ from, to }) asks the parent to load another range (both optional
// local dates, at most 92 days apart). Either may reject with a FeedApiError;
// its code is shown translated.
// Money is in each location's own currency, never the viewer's.
// The feed's net is the one the Daily Log counts (net when above 0, else
// gross), comparable with the typed net; the raw net shows under it when they
// differ, with a chip for fully discounted orders (the .NET host's difference
// 31, FEED_ALIGN_STATUS.md section 1). A replace that moved nothing (a keep
// undone on a day with no feed order: difference 32) says the typed rows stay.
// ═══════════════════════════════════════════════════════════════════════════
import { useState } from "react";
import { useT } from "../lib/i18n";
import { daysBetween, isDay, fmtDay, fmtDateTime, fmtMoney, sourceKey } from "../lib/feedUi";
import {
  inputStyle, adminThStyle, adminTdStyle, codeStyle, btnPrimaryDark, btnSecondary, smallBtn, disabledLook,
  mutedText, labelStyle, fieldErrorStyle, emptyRowStyle, tableStyle, theadRowStyle, rowBorder,
  Banner, FeedErrorText, Chip,
} from "./feedKit";

export const MAX_CHOICES = 366;   // contract 5.10
export const MAX_RANGE_DAYS = 92; // contract 5.9

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
const keyOf = (c) => `${c.channelId}\n${c.logDate}`;
// What the server recorded: replaced unless explicitly kept.
const savedKeep = (c) => c.choice === "keep";
// A replace with nothing replaced and no feed order: the typed rows were never
// moved out (they stay in the Daily Log, locked) until the day's first order.
const heldTyped = (c) => !savedKeep(c) && c.feed.orders === 0 && c.typed.rows === 0;

export function FeedConflicts({ conflicts = [], from = null, to = null, workspaces = [], onSave, onRange }) {
  const { t } = useT();
  const [draft, setDraft] = useState({});   // keyOf(day) → true (keep typed rows) | false (replaced by the feed)
  const [rangeFrom, setRangeFrom] = useState(from || "");
  const [rangeTo, setRangeTo] = useState(to || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const wsById = new Map(workspaces.map((w) => [w.id, w]));
  const keepOf = (c) => (has(draft, keyOf(c)) ? draft[keyOf(c)] : savedKeep(c));
  const dirty = conflicts.filter((c) => keepOf(c) !== savedKeep(c));
  // As saved, and the same split as each row's status: kept, held (a replace
  // with no feed order yet, the typed rows stay), or replaced by the feed.
  const keptCount = conflicts.filter(savedKeep).length;
  const heldCount = conflicts.filter(heldTyped).length;
  const replacedCount = conflicts.length - keptCount - heldCount;
  const anyNotKept = conflicts.some((c) => !keepOf(c));
  const tooMany = dirty.length > MAX_CHOICES;
  const canSave = dirty.length > 0 && !tooMany && !busy;

  const span = daysBetween(rangeFrom, rangeTo);
  const rangeOk = (rangeFrom === "" || isDay(rangeFrom)) && (rangeTo === "" || isDay(rangeTo))
    && (span == null || (span >= 0 && span <= MAX_RANGE_DAYS));

  const setKeep = (c, keep) => setDraft((d) => ({ ...d, [keyOf(c)]: keep }));
  const keepAllShown = () => setDraft((d) => {
    const next = { ...d };
    for (const c of conflicts) next[keyOf(c)] = true;
    return next;
  });

  async function run(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!canSave) return;
    const choices = dirty.map((c) => ({ channelId: c.channelId, logDate: c.logDate, choice: keepOf(c) ? "keep" : "replace" }));
    if (await run(() => onSave(choices))) setDraft({});
  }

  async function showRange() {
    if (!rangeOk || busy || !onRange) return;
    await run(() => onRange({ from: rangeFrom || null, to: rangeTo || null }));
  }

  return (
    <div>
      <div style={{ fontSize:12.5, color:"#475569", lineHeight:1.55, marginBottom:12 }}>{t("feed.conf.intro")}</div>

      {/* ── Range ─────────────────────────────────────────────────── */}
      <div style={{ display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap", marginBottom:12 }}>
        <div>
          <label style={labelStyle}>
            {t("feed.conf.from")}
            <input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} disabled={busy}
              style={{ ...inputStyle, display:"block", marginTop:5, width:160 }} />
          </label>
        </div>
        <div>
          <label style={labelStyle}>
            {t("feed.conf.to")}
            <input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} disabled={busy}
              style={{ ...inputStyle, display:"block", marginTop:5, width:160 }} />
          </label>
        </div>
        <button type="button" onClick={showRange} disabled={!rangeOk || busy || !onRange}
          style={disabledLook(btnSecondary, !rangeOk || busy || !onRange)}>
          {t("feed.conf.show")}
        </button>
        {conflicts.length > 0 && (
          <span style={{ marginInlineStart:"auto", display:"inline-flex", gap:6, flexWrap:"wrap" }}>
            <Chip tone="blue">{t("feed.conf.replacedCount")}<span className="num">{replacedCount}</span></Chip>
            {heldCount > 0 && <Chip tone="grey">{t("feed.conf.heldCount")}<span className="num">{heldCount}</span></Chip>}
            <Chip tone={keptCount > 0 ? "amber" : "grey"}>{t("feed.conf.keptCount")}<span className="num">{keptCount}</span></Chip>
          </span>
        )}
      </div>
      {!rangeOk && <div style={{ ...fieldErrorStyle, marginTop:-6, marginBottom:10 }}>{t("feed.conf.rangeInvalid")}</div>}

      {conflicts.length === 0 ? (
        <div style={emptyRowStyle}>{t("feed.conf.empty")}</div>
      ) : (
        <>
          {anyNotKept && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
              <button type="button" onClick={keepAllShown} disabled={busy} style={disabledLook(smallBtn, busy)}>{t("feed.conf.allKeep")}</button>
            </div>
          )}
          <div style={{ overflowX:"auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr style={theadRowStyle}>
                  <th style={adminThStyle}>{t("feed.conf.colDate")}</th>
                  <th style={adminThStyle}>{t("feed.conf.colLocation")}</th>
                  <th style={adminThStyle}>{t("feed.conf.colChannel")}</th>
                  <th style={adminThStyle}>{t("feed.conf.colTyped")}</th>
                  <th style={adminThStyle}>{t("feed.conf.colFeed")}</th>
                  <th style={adminThStyle}>{t("feed.conf.colChoice")}</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map((c, i) => {
                  const w = wsById.get(c.workspaceId);
                  const ch = w ? w.channels.find((x) => x.id === c.channelId) : null;
                  const currency = w ? w.currency : null;
                  const keep = keepOf(c);
                  const changed = keep !== savedKeep(c);
                  const sources = c.typed.sources.map((s) => (sourceKey(s) ? t(sourceKey(s)) : s)).join(", ");
                  // What counts for this day now (as saved), and since when.
                  const stateKey = savedKeep(c) ? "feed.conf.statusKept" : heldTyped(c) ? "feed.conf.statusHeld" : "feed.conf.statusReplaced";
                  const status = t(stateKey) + (c.decidedAt ? ` · ${fmtDateTime(c.decidedAt)}` : "");
                  // The net the Daily Log counts; the raw net only when it differs.
                  const counted = c.feed.netSalesCounted != null ? c.feed.netSalesCounted : c.feed.netSales;
                  const showRaw = c.feed.netSalesCounted != null && c.feed.netSales != null && c.feed.netSales !== c.feed.netSalesCounted;
                  const zeroNet = c.feed.fullyDiscountedOrders > 0 ? c.feed.fullyDiscountedOrders : 0;
                  return (
                    <tr key={keyOf(c)} style={{ ...rowBorder(i), background: changed ? "#eff6ff" : "transparent" }}>
                      <td className="num" style={{ ...adminTdStyle, whiteSpace:"nowrap", fontWeight:600, color:"#0f172a" }}>{fmtDay(c.logDate)}</td>
                      <td style={adminTdStyle}>{w ? (w.name || w.id) : "—"}</td>
                      <td style={adminTdStyle}>
                        <div>{ch ? (ch.platform || ch.id) : "—"}</div>
                        {c.platformCode && <code style={codeStyle}>{c.platformCode}</code>}
                      </td>
                      <td className="num" style={{ ...adminTdStyle, fontSize:12, lineHeight:1.6 }}>
                        <div>{t("feed.conf.rows")}{c.typed.rows}{sources ? <span style={mutedText}> · {sources}</span> : null}</div>
                        <div>{t("feed.conf.gross")}{fmtMoney(c.typed.gross, currency)}</div>
                        <div>{t("feed.conf.net")}{fmtMoney(c.typed.netSales, currency)}</div>
                        <div>{t("feed.conf.orders")}{c.typed.orders == null ? "—" : c.typed.orders}</div>
                      </td>
                      <td className="num" style={{ ...adminTdStyle, fontSize:12, lineHeight:1.6 }}>
                        <div>{t("feed.conf.orders")}{c.feed.orders}</div>
                        <div>{t("feed.conf.gross")}{fmtMoney(c.feed.gross, currency)}</div>
                        <div>{t("feed.conf.net")}{fmtMoney(counted, currency)}</div>
                        {showRaw && <div style={mutedText}>{t("feed.conf.netRaw")}{fmtMoney(c.feed.netSales, currency)}</div>}
                        {zeroNet > 0 && (
                          <div style={{ marginTop:3 }}><Chip tone="amber">{t("feed.conf.fullyDiscounted")}<span className="num">{zeroNet}</span></Chip></div>
                        )}
                      </td>
                      <td style={{ ...adminTdStyle, fontSize:12.5 }}>
                        <label style={{ display:"flex", gap:6, alignItems:"center", cursor: busy ? "default" : "pointer" }}>
                          <input type="checkbox" checked={keep} disabled={busy} onChange={(e) => setKeep(c, e.target.checked)} />
                          {t("feed.conf.keepOverride")}
                        </label>
                        <div style={{ ...mutedText, marginTop:5 }}>{status}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Save bar ───────────────────────────────────────────────── */}
      {error && <Banner kind="error" style={{ marginTop:10 }}><FeedErrorText error={error} /></Banner>}
      {tooMany && <div style={{ ...fieldErrorStyle, marginTop:10 }}>{t("feed.conf.tooMany")}</div>}
      {conflicts.length > 0 && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:10, marginTop:12, flexWrap:"wrap" }}>
          {dirty.length > 0 && (
            <span style={{ fontSize:12, color:"#64748b" }}>
              {t("feed.map.changes")}<span className="num" style={{ fontWeight:700, color:"#0f172a" }}>{dirty.length}</span>
            </span>
          )}
          {dirty.length > 0 && (
            <button type="button" onClick={() => { setDraft({}); setError(null); }} disabled={busy} style={disabledLook(btnSecondary, busy)}>
              {t("feed.map.undo")}
            </button>
          )}
          <button type="button" onClick={save} disabled={!canSave} style={disabledLook(btnPrimaryDark, !canSave)}>
            {busy ? t("feed.saving") : t("feed.conf.save")}
          </button>
        </div>
      )}
    </div>
  );
}
